<script lang="ts" setup>
import { ref, computed, reactive, watch, watchEffect } from 'vue';
import { useQuery, useMutation, useApolloClient } from '@vue/apollo-composable';
import {
  BucketDetailsDocument,
  BucketType,
  DataObjectType,
  SamplesAutoAnnotateDocument,
  SampleNamesFromFilesDocument,
  UserType,
} from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BCollapse,
  BContainer,
  BFormCheckbox,
  BModal,
  BSpinner,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { getFileDownloadUrl } from '@/utils/fileService';
import { formatFileSize, formatFileName } from '@/utils/file-utils';
import { isBclRunArchive } from '@/utils/bcl-to-fastq-input';
import { deepParseDates, formatDate } from '@/utils/date-utils';
import { truncateText } from '@/utils/string-utils';
import DataObjectRename from '@/components/data/DataObjectRename.vue';
import DataObjectDetail from '@/components/data/DataObjectDetail.vue';
import DataObjectDelete from '@/components/data/DataObjectDelete.vue';
import ProjectList, {
  ProjectTypeExtended,
} from '@/components/project/ProjectList.vue';
import SampleForm from '@/components/sample/SampleForm.vue';
import SampleList, {
  SampleTypeExtended,
} from '@/components/sample/SampleList.vue';
import {
  BUCKET_DATA_TYPES,
  IS_DEBUG_MODE,
  SHARING_USER_ROLES,
  TRUNCATE_TEXT_LENGTH,
} from '@/constants';
import BucketUserAssociationCreate from '@/components/data/BucketUserAssociationCreate.vue';
import BucketUserAssociationDelete from '@/components/data/BucketUserAssociationDelete.vue';
import BucketUpdate from '@/components/data/BucketUpdate.vue';
import BucketDelete from '@/components/data/BucketDelete.vue';
import ComputeTaskList from '@/components/compute/ComputeTaskList.vue';
import BclToFastqNew from '@/components/compute/BclToFastqNew.vue';
import { useUserStore } from '@/store/user';
import { useRouter } from 'vue-router';

const userStore = useUserStore();
const router = useRouter();
const { resolveClient } = useApolloClient();

// --- Component references ---
const computeTaskList = ref<InstanceType<typeof ComputeTaskList>>();

// --- Component properties ---
const props = defineProps<{
  bucketId: number;
  showMetadata: boolean;
  role: string;
  allowDownload: boolean;
  listComputeTasks: boolean;
  listSamples: boolean;
  listProjects: boolean;
  listUsers: boolean;
  pathPrefix: string; // The current "folder" path
}>();

const emits = defineEmits(['update:pathPrefix', 'bucketDeleted']);

const canEdit = computed(() => {
  return (
    props.role === SHARING_USER_ROLES.OWNER ||
    props.role === SHARING_USER_ROLES.EDITOR
  );
});

// --- Define Interfaces for BreadCrumbItem and SelectableDataObjectType ---
interface BreadCrumbItem {
  path: string;
  name: string;
}
interface SelectableDataObjectType extends DataObjectType {
  isSelectable: boolean;
  selected: boolean;
}

// --- Local States ---
//const storageBucket = ref<BucketType | null>(null);
const showAdvancedFeatures = ref(false);
const showBucketUpdateModal = ref(false);
const showBucketDeleteModal = ref(false);
const showBucketExpirationModal = ref(false);
const showBucketUserAssociationCreateModal = ref(false);
const showBucketUserAssociationDeleteModal = ref(false);
const showCompleteDescription = ref(false);
const selectedUser = ref<UserType | null>(null);
const selectableDataObjects = ref<SelectableDataObjectType[]>([]);
const currentPathBreadCrumbs = computed(() => {
  let breadCrumbs: Array<BreadCrumbItem> = [];
  if (storageBucket.value?.name) {
    breadCrumbs.push({
      path: '',
      name: storageBucket.value.friendlyName
        ? storageBucket.value.friendlyName
        : storageBucket.value?.name,
    });
  }
  const pathParts = props.pathPrefix.split('/').filter((part) => part) ?? [];
  for (let i = 0; i < pathParts.length; i++) {
    const path = pathParts.slice(0, i + 1).join('/') + '/';
    const name = pathParts[i];
    breadCrumbs.push({
      path: path,
      name: name,
    });
  }
  return breadCrumbs;
});

// --- Storage objects selection (details, rename, download, delete) ---
const selectedDataObject = ref<SelectableDataObjectType | null>(null);
const DataObjectNameUpdate = ref('');
const showModalDataObjectDetail = ref(false);
const showModalDataObjectRename = ref(false);
const showModalDataObjectDelete = ref(false);
const showModalSampleCreate = ref(false);
const showModalBclToFastq = ref(false);

const selectedDataObjectPreviewEnabled = computed(() => {
  // if allowDownload is true, then allow preview
  if (props.allowDownload) {
    return true;
  }
  // otherwise only allow preview for the selected text files
  if (selectedDataObject.value && selectedDataObject.value.name) {
    return (
      selectedDataObject.value.name?.endsWith('.html') ||
      selectedDataObject.value.name?.endsWith('.htm') ||
      selectedDataObject.value.name?.endsWith('.log') ||
      selectedDataObject.value.name?.endsWith('.txt') ||
      selectedDataObject.value.name?.endsWith('.csv') ||
      selectedDataObject.value.name?.endsWith('.json') ||
      selectedDataObject.value.name?.endsWith('.tsv') ||
      selectedDataObject.value.name?.endsWith('.xml') ||
      selectedDataObject.value.name?.endsWith('.yml') ||
      selectedDataObject.value.name?.endsWith('.yaml') ||
      selectedDataObject.value.name?.endsWith('.md') ||
      selectedDataObject.value.name?.endsWith('.sql') ||
      selectedDataObject.value.name?.endsWith('.pdf')
    );
  }
  // otherwise, return false
  return false;
});

const newSampleName = ref('');
const newSampleDescription = ref('');
const newSampleFileR1 = ref('');
const newSampleFileR2 = ref('');
const newSampleKitCode = ref('');
const newSampleWarning = ref<string | null>(null);

const newSampleError = ref<string | null>(null);
const newSampleQueryInProgress = ref(false);
const newSampleEnabled = computed(() => selectedDataObjects.value.length === 2);

const bclToFastqEnabled = computed(() => {
  if (selectedDataObjects.value.length === 1) {
    return isBclRunArchive(selectedDataObjects.value[0]?.name);
  } else if (selectedDataObjects.value.length === 2) {
    let hasBcl = false;
    let hasCsv = false;
    for (const sObj of selectedDataObjects.value) {
      if (sObj.name && isBclRunArchive(sObj.name)) {
        hasBcl = true;
      } else if (sObj.name && sObj.name.toLowerCase().endsWith('csv')) {
        hasCsv = true;
      }
    }
    return hasBcl && hasCsv;
  }
  return false;
});

const conversionFileBcl = computed(() => {
  for (const sObj of selectedDataObjects.value) {
    if (sObj.name && isBclRunArchive(sObj.name)) {
      return sObj.name;
    }
  }
  return null;
});

const conversionFileCsv = computed(() => {
  for (const sObj of selectedDataObjects.value) {
    if (sObj.name && sObj.name.toLowerCase().endsWith('csv')) {
      return sObj.name;
    }
  }
  return null;
});

const conversionDefaultName = ref('BCL to FASTQ conversion');

function setConversionDefaultName() {
  let defaultName = 'BCL to FASTQ conversion 1';
  let taskNo = 1;
  let nameUnique = false;
  while (!nameUnique) {
    defaultName = `BCL to FASTQ conversion ${taskNo}`;
    nameUnique = !computeTaskList.value?.tasks.some(
      (obj) => obj.name === defaultName
    );
    taskNo++;
  }
  conversionDefaultName.value = defaultName;
}

// --- Table Fields ---
const fileTableFields = [
  {
    key: 'id',
    label: '',
    tdClass: 'align-middle min-width',
    thClass: 'min-width',
    width: '1px',
  },
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    formatter: formatFileName,
    tdClass: 'align-middle',
  },
  {
    key: 'size',
    label: 'Size',
    sortable: true,
    formatter: formatFileSize,
    tdClass: 'align-middle',
  },
  {
    key: 'updatedAt',
    label: 'Last updated',
    sortable: true,
    tdClass: 'align-middle',
  },
  { key: 'actions', label: '', tdClass: 'align-middle text-end' },
];

// --- Query the bucket's files ---
const {
  result: result,
  loading: loading,
  error: error,
  refetch: refetch,
} = useQuery(
  BucketDetailsDocument,
  {
    bucketId: props.bucketId,
    pathPrefix: props.pathPrefix,
  },
  {
    fetchPolicy: 'no-cache',
  }
);

function requestRefetchDetails(bucketId: number, pathPrefix: string) {
  console.debug('Refetching', pathPrefix);
  refetch({
    bucketId: bucketId,
    pathPrefix: pathPrefix,
  })
    ?.catch((err) => {
      console.error('Refetch failed:', err);
    })
    ?.then(() => {
      console.log('Bucket details refetched');
    });
}

// --- Re-run the query whenever path changes ---
watch(
  () => props.pathPrefix,
  (newPath) => {
    requestRefetchDetails(props.bucketId, newPath);
  }
);

const storageBucket = computed(() => {
  return result.value?.bucket
    ? (deepParseDates(result.value.bucket) as BucketType)
    : null;
});

const isSystemBucket = computed(() => {
  return storageBucket.value?.dataType === BUCKET_DATA_TYPES.SYSTEM;
});

// When storageBucket.value or props.pathPrefix change, update the reactive state
watchEffect(() => {
  if (result.value && storageBucket.value && storageBucket.value.dataObjects) {
    // Create a new array based on the latest data
    selectableDataObjects.value = storageBucket.value.dataObjects
      .filter((obj) => obj && obj.name !== props.pathPrefix)
      .map((obj) =>
        reactive({
          ...obj,
          isSelectable: true,
          selected: false, // default value, mutable later with checkbox events
        })
      );
    // If we're not at the root, add a synthetic parent folder row.
    if (props.pathPrefix) {
      selectableDataObjects.value.unshift({
        id: '-1',
        name: '..',
        isFolder: true,
        size: null,
        updatedAt: null,
        isSelectable: false,
        selected: false,
      });
    }
  } else {
    selectableDataObjects.value = [];
  }
});

const selectedDataObjects = computed<SelectableDataObjectType[]>(() => {
  console.debug(
    'Selected data objects:',
    selectableDataObjects.value.filter((obj) => obj.selected)
  );
  return selectableDataObjects.value.filter((obj) => obj.selected);
});

const noDataObjects = computed(() => {
  return selectableDataObjects.value.length === 0;
});

const samples = computed<SampleTypeExtended[]>(() => {
  const sList: SampleTypeExtended[] = [];
  storageBucket.value?.samples?.forEach((sample) => {
    if (sample) {
      sList.push({
        ...sample,
        canEdit: canEdit.value,
      });
    }
  });
  return sList;
});

const projects = computed<ProjectTypeExtended[]>(() => {
  let projects: ProjectTypeExtended[] = [];
  // iterate through sample and their associated projects
  for (const sample of samples.value) {
    for (const assoc of sample.projectsAssociated ?? []) {
      if (assoc?.project) {
        // only add the project if project with the same ID is not yet in the collection
        if (!projects.find((p) => p.id === assoc.project?.id)) {
          projects.push({
            ...assoc?.project,
            canEdit: false, // no need for delete button
          });
        }
      }
    }
  }
  // return projects
  return projects;
});

// --- Handle Storage Object Actions / Bucket Browsing ---
function updatePathPrefix(path: string, evt?: MouseEvent) {
  // Ctrl (Win/Linux) or Cmd (macOS) → open in new tab
  // Also support middle-click if it bubbles as a click (evt.button === 1)
  if (evt && (evt.ctrlKey || evt.metaKey || evt.button === 1)) {
    const to = {
      name: 'BucketDetail',
      params: {
        id: props.bucketId,
      },
      query: path ? { path } : undefined, // put the folder path in query
    };
    const url = router.resolve(to).href;
    window.open(url, '_blank', 'noopener'); // new tab, no opener
    evt.preventDefault();
    return;
  }

  // Normal left-click → navigate in-place by emitting the event
  emits('update:pathPrefix', path);
  console.debug('Updating path prefix to:', path);
}

function openFolder(folderName: string, evt?: MouseEvent) {
  // emits('update:pathPrefix', folderName);
  console.debug('Opening folder:', folderName);
  updatePathPrefix(folderName, evt);
}

function goUpOneFolder(evt?: MouseEvent) {
  // check if we are at the root
  if (!props.pathPrefix) return;
  // go up one folder
  const parts = props.pathPrefix.split('/');
  parts.pop(); // removes last empty part
  parts.pop(); // removes last folder
  if (parts.length === 0) {
    // emits('update:pathPrefix', '');
    console.debug('Going up to root');
    updatePathPrefix('', evt);
    return;
  }
  // emits('update:pathPrefix', parts.join('/') + '/'); // always add trailing slash
  console.debug('Going up to:', props.pathPrefix);
  updatePathPrefix(parts.join('/') + '/', evt); // always add trailing slash
}

function handleDataObjectDetailRequest(
  DataObject: SelectableDataObjectType,
  evt?: MouseEvent
) {
  console.log('Clicked on object:', DataObject);
  if (DataObject.isFolder) {
    if (DataObject.name) {
      openFolder(DataObject.name, evt);
    } else {
      console.error('No folder name found in object:', DataObject);
    }
  } else {
    selectedDataObject.value = DataObject;
    showModalDataObjectDetail.value = true;
  }
}

function dataRowClicked(
  dataObject: SelectableDataObjectType,
  index?: number,
  evt?: MouseEvent
) {
  // check if the object is a folder
  if (dataObject.name === '..') {
    // if it is the parent folder, go up one folder
    goUpOneFolder(evt);
  } else if (dataObject.isFolder && dataObject.name) {
    // if it is a folder, open it
    openFolder(dataObject.name, evt);
  } else if (!dataObject.isFolder) {
    // if it is a file, show the details
    handleDataObjectDetailRequest(dataObject, evt);
  }
}

async function handleDataObjectDownloadRequest(
  DataObject: SelectableDataObjectType
) {
  // validate the selected data object
  if (!DataObject.name) {
    console.error('No name found in selected data object');
    return;
  }
  // fetch the download URL
  const url = await getFileDownloadUrl(props.bucketId, DataObject.name);
  if (!url) {
    console.error(
      `Unexpected error fetching download URL for the file ${DataObject.name}`
    );
    alert(
      'Failed to generate download link. The requested file might not be available anymore.'
    );
    return;
  }
  // Open the URL in a new tab to trigger the download.
  window.open(url, '_blank');
}

function handleDataObjectRenameRequest(DataObject: SelectableDataObjectType) {
  if (DataObject.name) {
    selectedDataObject.value = DataObject;
    DataObjectNameUpdate.value = formatFileName(DataObject.name);
    showModalDataObjectRename.value = true;
  } else {
    console.error(
      'Storage object rename requested, but no name found in object:',
      DataObject
    );
  }
}

function handleDataObjectDeleteRequest(DataObject: SelectableDataObjectType) {
  selectedDataObject.value = DataObject;
  showModalDataObjectDelete.value = true;
}

function refreshRequested() {
  requestRefetchDetails(props.bucketId, props.pathPrefix);
}

function onDataObjectRenameSuccess() {
  closeModals();
  requestRefetchDetails(props.bucketId, props.pathPrefix);
}

function onDataObjectDeleteSuccess() {
  closeModals();
  requestRefetchDetails(props.bucketId, props.pathPrefix);
}

function onSampleFormSuccess() {
  closeModals();
  requestRefetchDetails(props.bucketId, props.pathPrefix);
}

function handleDataObjectsSelectionDelete() {
  // TODO: Implement the batch delete logic here
  alert('Batch delete not implemented');
}

// -- Sample Auto-Annotation Action ---
const annotationMutationInProgress = ref(false);
const annotationMutationShowSuccess = ref(false);
const annotationMutationError = ref<string | null>(null);

const {
  mutate: executeSampleAutoAnnotationMutation,
  onDone: onSampleAutoAnnotationDone,
  onError: onSampleAutoAnnotationError,
} = useMutation(SamplesAutoAnnotateDocument);

onSampleAutoAnnotationDone((res) => {
  annotationMutationInProgress.value = false;
  if (res.data && res.data.samplesAutoAnnotate) {
    console.debug(
      'Sample auto-annotation successful:',
      res.data.samplesAutoAnnotate
    );
    annotationMutationShowSuccess.value = true;
    setTimeout(() => {
      annotationMutationShowSuccess.value = false;
      requestRefetchDetails(props.bucketId, props.pathPrefix);
    }, 1000);
  } else {
    annotationMutationError.value =
      'Sample auto-annotation failed: ' + res.data;
    console.error('Sample auto-annotation failed:', res.data);
  }
});

onSampleAutoAnnotationError((err) => {
  console.error('Error occured while auto-annotating samples:', err);
  annotationMutationInProgress.value = false;
  annotationMutationError.value = err.message;
});

function handleSampleAutoAnnotation() {
  console.debug('Sample auto-annotation requested');

  annotationMutationInProgress.value = true;
  annotationMutationError.value = null;

  const filesForAnnotation: string[] = [];

  if (selectedDataObjects.value.length > 0) {
    selectedDataObjects.value
      .filter((obj) => !obj.isFolder && obj.name)
      .forEach((obj) => {
        if (obj.name) {
          filesForAnnotation.push(obj.name);
        }
      });
  }

  executeSampleAutoAnnotationMutation({
    bucketId: props.bucketId,
    pathPrefix: props.pathPrefix,
    recursive: false,
    files: filesForAnnotation.length > 0 ? filesForAnnotation : null,
  });
}

// -- BCL to FASTQ conversion action ---
function handleBclToFastq() {
  console.debug('BCL to FASTQ conversion requested');
  setConversionDefaultName();
  showModalBclToFastq.value = true;
}

// --- Bucket Expiration Action ---
function handleBucketExpirationDetailRequest() {
  showBucketExpirationModal.value = true;
}

// --- New Sample Action ---
async function handleNewSample() {
  newSampleWarning.value = newSampleError.value = null;
  newSampleName.value = newSampleDescription.value = '';
  const files = selectedDataObjects.value.map((obj) => obj.name || '');
  [newSampleFileR1.value, newSampleFileR2.value] = files;
  newSampleQueryInProgress.value = true;
  try {
    const { data } = await resolveClient().query({
      query: SampleNamesFromFilesDocument,
      variables: { files },
      fetchPolicy: 'no-cache',
    });
    const preview = data.sampleNamesFromFiles;
    if (!preview) throw new Error('Could not preview sample names. Try again.');
    const [sample] = preview.samples;
    if (preview.samples.length > 1) {
      newSampleError.value = 'Selected files belong to different samples.';
    } else if (sample?.fileR1 && sample.fileR2) {
      newSampleName.value = sample.name;
      newSampleFileR1.value = sample.fileR1;
      newSampleFileR2.value = sample.fileR2;
    } else if (preview.unmatchedFiles.length) {
      newSampleWarning.value =
        'Could not determine sample name. Enter a name and check the R1/R2 files.';
    } else {
      newSampleError.value = 'Selected files must contain one R1 and one R2.';
    }
  } catch (error) {
    newSampleError.value = (error as Error).message;
  } finally {
    newSampleQueryInProgress.value = false;
  }
  // set default kit code based on the storage coupon codes used
  console.debug(
    'Coupons associated with bucket:',
    storageBucket.value?.couponsAssociated
  );
  const storageCouponAssociation = storageBucket.value?.couponsAssociated?.find(
    (assoc) =>
      assoc &&
      assoc.coupon &&
      assoc.coupon.code &&
      assoc.coupon.couponType === 'storage' &&
      (!assoc.coupon.expiresAt || assoc.coupon.expiresAt >= Date.now())
  );
  console.debug(
    'Default coupon association to be used with the upload:',
    storageCouponAssociation
  );
  newSampleKitCode.value = storageCouponAssociation?.coupon?.code?.code || '';
  // show the new sample modal
  showModalSampleCreate.value = true;
}

function closeModals() {
  // close all storage objects modals
  showModalDataObjectDetail.value = false;
  showModalDataObjectRename.value = false;
  showModalDataObjectDelete.value = false;
  showModalSampleCreate.value = false;
  showBucketUserAssociationCreateModal.value = false;
  showBucketUserAssociationDeleteModal.value = false;
  showBucketUpdateModal.value = false;
  showBucketDeleteModal.value = false;
  showModalBclToFastq.value = false;
  showBucketExpirationModal.value = false;

  // reset selected file and sample
  selectedDataObject.value = null;
  selectedUser.value = null;
  newSampleName.value = '';
}

function bucketUserAssociationDeleteRequested(user: UserType) {
  selectedUser.value = user;
  showBucketUserAssociationDeleteModal.value = true;
}

function onBucketSharingChangedSuccess() {
  closeModals();
  requestRefetchDetails(props.bucketId, props.pathPrefix);
}

function onBucketUpdatedSuccess() {
  closeModals();
  requestRefetchDetails(props.bucketId, props.pathPrefix);
}

function onBucketDeletedSuccess() {
  closeModals();
  emits('bucketDeleted');
}

function onBclToFastqSuccess() {
  closeModals();
  requestRefetchDetails(props.bucketId, props.pathPrefix);
}
</script>
<template>
  <div>
    <!-- Error fetching bucket, show an alert -->
    <b-alert v-model="error" show variant="danger">
      An error occurred while loading bucket data:
      {{ error?.message }}
    </b-alert>

    <!-- If loading, show spinner -->
    <div
      v-if="loading"
      class="text-center"
      style="
        min-height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
      "
    >
      <b-spinner label="Loading data..." />
    </div>

    <!-- Loading finished -->
    <div v-else-if="storageBucket">
      <!-- Bucket Name + Metadata -->
      <template v-if="props.showMetadata">
        <!-- Bucket Name -->
        <h1>
          <font-awesome-icon :icon="['fas', 'database']" class="me-2" />
          <span class="me-2">{{ storageBucket.friendlyName }}</span>
          <b-button-group class="p-0 link-dark align-baseline btn-xs">
            <b-button
              v-if="canEdit"
              @click="showBucketUpdateModal = true"
              v-b-tooltip.hover
              title="Edit Bucket Details"
              variant="dark"
            >
              <font-awesome-icon :icon="['fas', 'pencil']" />
            </b-button>

            <template v-if="!isSystemBucket">
              <!-- User Role Badge -->
              <b-button
                v-if="props.role === SHARING_USER_ROLES.OWNER"
                variant="warning"
              >
                <i class="fas fa-crown"></i>
                {{ props.role }}
              </b-button>
              <b-button
                v-else-if="props.role === SHARING_USER_ROLES.EDITOR"
                variant="danger"
              >
                <i class="fas fa-pencil"></i>
                {{ props.role }}
              </b-button>
              <b-button v-else variant="success">
                <i class="fas fa-eye"></i>
                viewer
              </b-button>

              <!-- Creation Date-->
              <b-button
                variant="secondary"
                v-b-tooltip.hover
                title="Creation Date"
              >
                <i class="fas fa-calendar"></i>
                {{ formatDate(storageBucket.createdAt) }}
              </b-button>

              <!-- Expiration Date (danger variant if expiration in < 7 days)-->
              <b-button
                @click="handleBucketExpirationDetailRequest"
                :variant="
                  storageBucket.expiresAt &&
                  storageBucket.expiresAt - Date.now() < 7 * 24 * 60 * 60 * 1000
                    ? 'danger'
                    : 'secondary'
                "
                v-if="storageBucket.expiresAt"
                v-b-tooltip.hover
                title="Expiration Date"
                style="border-left: 1px solid rgba(255, 255, 255, 0.3)"
              >
                <font-awesome-icon :icon="['fas', 'hourglass-end']" />
                {{ formatDate(storageBucket.expiresAt) }}
              </b-button>
            </template>
          </b-button-group>
        </h1>
        <hr />
        <!-- Bucket Description -->
        <p v-if="storageBucket.description">
          {{
            showCompleteDescription
              ? storageBucket.description
              : truncateText(storageBucket.description, TRUNCATE_TEXT_LENGTH)
          }}
          <BButton
            v-if="
              canEdit &&
              (showCompleteDescription ||
                storageBucket.description.length <= TRUNCATE_TEXT_LENGTH)
            "
            @click="showBucketUpdateModal = true"
            variant="link"
            class="p-0 ms-2 link-dark align-baseline"
            size="sm"
          >
            <font-awesome-icon :icon="['fas', 'pencil']" />
          </BButton>
          <template
            v-if="storageBucket.description.length > TRUNCATE_TEXT_LENGTH"
          >
            <span v-if="!showCompleteDescription">&nbsp;</span>
            <br v-else />
            <b-button
              variant="link"
              class="p-0 align-baseline link-secondary text-decoration-underline"
              :class="{ 'mt-2': showCompleteDescription }"
              @click="showCompleteDescription = !showCompleteDescription"
              :aria-expanded="showCompleteDescription"
            >
              {{ showCompleteDescription ? 'Show Less' : 'Show More' }}
            </b-button>
          </template>
        </p>

        <!-- Edit Bucket Details (name and description) -->
        <BucketUpdate
          v-if="
            canEdit &&
            showBucketUpdateModal &&
            storageBucket &&
            storageBucket.name
          "
          :show="showBucketUpdateModal"
          :bucketId="props.bucketId"
          :bucketName="storageBucket.name"
          :bucketFriendlyName="storageBucket.friendlyName || ''"
          :bucketDescription="storageBucket.description || ''"
          @close="showBucketUpdateModal = false"
          @closeAndRefresh="onBucketUpdatedSuccess"
        />
      </template>

      <!-- List of storage objects -->
      <h5 class="mt-4">
        <font-awesome-icon icon="folder" class="me-1" />
        Files
      </h5>
      <!-- File Path Breadcrumbs -->
      <div class="breadcrumbs mb-1" v-if="currentPathBreadCrumbs.length > 0">
        <!-- take non-bold breadcrumbs elements and glue them together with  `<strong> > </strong>` -->
        <span
          v-for="(crumb, index) in currentPathBreadCrumbs"
          :key="crumb.path"
        >
          <a @click="openFolder(crumb.path)" style="cursor: pointer">{{
            crumb.name
          }}</a>
          <font-awesome-icon
            size="sm"
            v-if="index < currentPathBreadCrumbs.length - 1"
            icon="angle-right"
            class="mx-1"
          />
        </span>
      </div>

      <!-- Upper FileBrowser Toolbar -->
      <BContainer
        v-if="storageBucket"
        :id="`toolbar-bucket-${storageBucket.id}`"
        class="file-toolbar d-flex flex-wrap align-items-center gap-1 px-1 py-1"
        fluid
      >
        <b-button
          @click="requestRefetchDetails(props.bucketId, pathPrefix)"
          variant="primary"
          size="sm"
        >
          <font-awesome-icon icon="sync" /> Refresh
        </b-button>
        <template v-if="!isSystemBucket">
          <!-- Share data button -->
          <b-button
            v-if="props.listUsers && canEdit"
            @click="showBucketUserAssociationCreateModal = true"
            variant="primary"
            size="sm"
          >
            <font-awesome-icon icon="user-plus" /> Share Data
          </b-button>

          <!-- New sample Button -->
          <span :id="`btn-new-sample-${storageBucket.id}`">
            <b-button
              @click="handleNewSample"
              v-if="
                canEdit && storageBucket.dataType === BUCKET_DATA_TYPES.INPUT
              "
              variant="primary"
              size="sm"
              :disabled="!newSampleEnabled || newSampleQueryInProgress"
            >
              <font-awesome-icon icon="plus" /> New Sample
            </b-button>
          </span>

          <!-- Sample Auto-Annotation Button -->
          <span :id="`btn-auto-annotate-samples-${storageBucket.id}`">
            <b-button
              @click="handleSampleAutoAnnotation"
              v-if="
                canEdit && storageBucket.dataType === BUCKET_DATA_TYPES.INPUT
              "
              variant="primary"
              size="sm"
              :disabled="annotationMutationInProgress"
            >
              <font-awesome-icon icon="wand-magic-sparkles" />

              <span v-if="annotationMutationInProgress">
                Updating Code
                <b-spinner small label=""></b-spinner>
              </span>
              <span v-else-if="annotationMutationShowSuccess">
                Samples Annotated
                <font-awesome-icon
                  :icon="['fa', 'check-double']"
                  class="me-2"
                />
              </span>
              <span v-else> Auto-Annotate Samples </span>
            </b-button>
          </span>

          <!-- BCL to FASTQ conversion Button -->
          <span :id="`btn-bcl-to-fastq-${storageBucket.id}`">
            <b-button
              @click="handleBclToFastq"
              v-if="
                canEdit && storageBucket.dataType === BUCKET_DATA_TYPES.INPUT
              "
              variant="primary"
              size="sm"
              :disabled="!bclToFastqEnabled"
            >
              <font-awesome-icon icon="shuffle" /> BCL to FASTQ
            </b-button>
          </span>
          <!-- Button tooltips -->
          <BTooltip :target="`btn-auto-annotate-samples-${storageBucket.id}`">
            This action will not modify already annotated samples or produce
            duplicates. Select specific files if you want to limit the scope of
            the operation.
          </BTooltip>
          <BTooltip :target="`btn-new-sample-${storageBucket.id}`">
            Select the R1 and R2 fastq files to manually annotate a sample
          </BTooltip>
          <BTooltip :target="`btn-bcl-to-fastq-${storageBucket.id}`"
            >Select BCL.tar archive file to enable this feature</BTooltip
          >
        </template>
        <!-- Batch Delete Button (not implemented yet -> hidden) -->
        <b-button
          @click="handleDataObjectsSelectionDelete"
          v-if="canEdit"
          variant="primary"
          size="sm"
          :disabled="!selectedDataObjects.length"
          hidden
        >
          <font-awesome-icon icon="trash" /> Delete
        </b-button>
      </BContainer>

      <!-- Error during auto sample annotation, show an alert -->
      <b-alert
        v-model="annotationMutationError"
        show
        variant="danger"
        dismissible
      >
        An error occurred while auto annotating samples:
        {{ annotationMutationError }}
      </b-alert>

      <!-- List of files -->
      <b-alert v-model="noDataObjects" variant="secondary"
        >No files found.</b-alert
      >
      <BCard v-if="selectableDataObjects.length > 0">
        <b-table
          :items="selectableDataObjects"
          :busy="loading"
          :fields="fileTableFields"
          small
          responsive
          hover
          class="browser-table"
          @row-clicked="dataRowClicked"
        >
          <template #cell(id)="data">
            <BFormCheckbox
              size="sm"
              v-if="data.item.isSelectable"
              v-model="data.item.selected"
            />
          </template>
          <template #cell(updatedAt)="data">
            {{ formatDate(data.value, '-') }}
          </template>
          <template #cell(name)="data">
            <font-awesome-icon
              v-if="data.item.isFolder"
              icon="folder"
              class="me-1 file-browser"
            />
            <font-awesome-icon v-else icon="file-lines" class="me-1" />
            {{ data.value }}
          </template>
          <template #cell(actions)="data">
            <BButtonGroup class="action-buttons">
              <BButton
                v-if="data.item.isFolder && data.item.name === '..'"
                size="sm"
                variant="outline-secondary"
                @click="goUpOneFolder"
              >
                <font-awesome-icon icon="arrow-left" class="me-1" />
              </BButton>
              <BButton
                v-else
                size="sm"
                variant="outline-secondary"
                @click="handleDataObjectDetailRequest(data.item, $event)"
              >
                <span v-if="data.item.isFolder">
                  <font-awesome-icon icon="folder" class="me-1" />
                </span>
                <span v-else>
                  <font-awesome-icon icon="info-circle" class="me-1" />
                </span>
              </BButton>
              <BButton
                size="sm"
                v-if="
                  props.allowDownload &&
                  data.item.isSelectable &&
                  !data.item.isFolder
                "
                variant="outline-secondary"
                @click="handleDataObjectDownloadRequest(data.item)"
              >
                <font-awesome-icon icon="download" class="me-1" />
              </BButton>
              <BButton
                size="sm"
                v-if="canEdit && data.item.isSelectable"
                variant="outline-secondary"
                @click="handleDataObjectRenameRequest(data.item)"
              >
                <font-awesome-icon icon="pencil" class="me-1" />
              </BButton>
              <BButton
                size="sm"
                v-if="canEdit && data.item.isSelectable"
                variant="outline-secondary"
                @click="handleDataObjectDeleteRequest(data.item)"
              >
                <font-awesome-icon icon="trash" class="me-1" />
              </BButton>
            </BButtonGroup>
          </template>
        </b-table>
      </BCard>

      <!-- List compute tasks -->
      <div v-if="props.listComputeTasks">
        <h5 class="mt-4">
          <font-awesome-icon icon="cogs" class="me-1" />
          Compute Tasks
        </h5>
        <ComputeTaskList
          ref="computeTaskList"
          parentName="Data Browser"
          :user-role="null"
          :bucketId="props.bucketId"
          :paging-items-per-page="0"
        />
      </div>

      <!-- List samples -->
      <div v-if="props.listSamples && samples">
        <h5 class="mt-4">
          <font-awesome-icon icon="flask" class="me-1" />
          Samples
        </h5>
        <SampleList
          :samples="samples"
          :loading="loading"
          :showBucket="false"
          :showKit="true"
          :showAssociatedProjects="false"
          :assocProjectId="null"
          :assocProjectName="null"
          :canEditSampleAssoc="false"
          @refresh="refreshRequested"
        />
        <p>
          <!-- Inform the user that in order to process and analyze the annotated samples, user needs to create project in data manager if no yet done, associate the samples with the project, and start sample processing from withing the project instead of the data manager. All eventual projects associated with the samples are listed below. -->
          To process and analyze annotated samples, first create a project in
          <router-link :to="{ name: 'Projects' }">Project Manager</router-link>
          (if you haven't already), associate the samples with that project, and
          then start processing from within the project. Projects already linked
          to these samples are listed below.
        </p>
      </div>

      <!-- List projects -->
      <div v-if="props.listProjects && projects">
        <h5 class="mt-4">
          <font-awesome-icon icon="project-diagram" class="me-1" />
          Projects
        </h5>
        <ProjectList
          :projects="projects"
          :loading="loading"
          :canEdit="canEdit"
          :showOwner="false"
        />
      </div>

      <!-- ADVANCED FEATURES - USERS, DELETE, DEBUG (show only to owners & editors) -->
      <template v-if="canEdit">
        <BButton
          @click="showAdvancedFeatures = !showAdvancedFeatures"
          class="mt-4"
          size="sm"
          variant="outline-dark"
        >
          <font-awesome-icon icon="cogs" class="me-1" />
          Advanced
          <font-awesome-icon
            :icon="showAdvancedFeatures ? 'angle-down' : 'angle-right'"
          />
        </BButton>
        <BCollapse v-model="showAdvancedFeatures">
          <!-- USERS -->
          <div v-if="props.listUsers && !isSystemBucket">
            <h5 class="mt-4">
              <font-awesome-icon icon="user-shield" class="me-1" />
              Users & Sharing
            </h5>
            <p v-if="storageBucket?.usersAssociated?.length === 0">
              No users associated with this bucket.
            </p>
            <template
              v-for="(assoc, idx) in storageBucket?.usersAssociated"
              :key="idx"
            >
              <p v-if="assoc && assoc.user">
                <strong>Role:</strong> {{ assoc.userRole }}<br />
                <strong>Name:</strong>
                {{ assoc.user?.givenName }}
                {{ assoc.user?.familyName }}
                <br />
                <strong>Email:</strong> {{ assoc.user?.email }}<br />
                <b-button
                  v-if="canEdit && assoc.userRole !== 'owner'"
                  @click="bucketUserAssociationDeleteRequested(assoc.user)"
                  variant="outline-danger"
                  size="sm"
                >
                  <font-awesome-icon :icon="['fas', 'times']" class="me-2" />
                  Remove
                </b-button>
              </p>
            </template>
            <BButton
              v-if="canEdit"
              @click="showBucketUserAssociationCreateModal = true"
              variant="secondary"
              size="sm"
            >
              <font-awesome-icon icon="user-plus" class="me-1" />
              Share data
            </BButton>
            <BucketUserAssociationCreate
              v-if="showBucketUserAssociationCreateModal"
              :show="showBucketUserAssociationCreateModal"
              :bucketId="props.bucketId"
              @close="closeModals"
              @closeAndRefresh="onBucketSharingChangedSuccess"
            />
            <BucketUserAssociationDelete
              v-if="showBucketUserAssociationDeleteModal && selectedUser"
              :show="showBucketUserAssociationDeleteModal"
              :bucketId="props.bucketId"
              :user="selectedUser"
              :bucket-friendly-name="storageBucket.friendlyName || ''"
              @close="closeModals"
              @close-and-refresh="onBucketSharingChangedSuccess"
            />
          </div>

          <!-- DEBUG INFO -->
          <div
            v-if="
              storageBucket.name &&
              bucketId &&
              (IS_DEBUG_MODE || userStore.developer)
            "
          >
            <h5 class="mt-4">
              <font-awesome-icon icon="info-circle" class="me-1" />
              Debug Info
            </h5>
            <p>
              <strong>Bucket ID:</strong> {{ bucketId }}<br />
              <strong>Bucket name:</strong> {{ storageBucket.name }}
            </p>
          </div>

          <!-- DELETE BUCKET -->
          <div v-if="!isSystemBucket">
            <h5 class="mt-4">
              <font-awesome-icon icon="trash" class="me-1" />
              Delete Data
            </h5>
            <p variant="warning" show>
              <strong>Warning:</strong> This action is irreversible. All data
              associated with this bucket will be permanently deleted.
            </p>
            <b-button
              @click="showBucketDeleteModal = true"
              variant="danger"
              size="sm"
            >
              <font-awesome-icon icon="trash" class="me-1" />
              Delete Data
            </b-button>

            <BucketDelete
              v-if="showBucketDeleteModal"
              :show="showBucketDeleteModal"
              :bucketId="props.bucketId"
              :bucketFriendlyName="storageBucket.friendlyName || ''"
              @close="closeModals"
              @close-and-refresh="onBucketDeletedSuccess"
            />
          </div>
        </BCollapse>
      </template>
      <!-- end of <template> with advanced feauters (shown only to admins and editors) -->
    </div>
    <!-- end of <div> shown on finished loading -->

    <!-- MODAL: Storage Object Detail (file-only) -->
    <div
      v-if="
        selectedDataObject &&
        storageBucket &&
        storageBucket.name &&
        storageBucket.id
      "
    >
      <DataObjectDetail
        :show="showModalDataObjectDetail"
        :data-object="selectedDataObject"
        :bucket-name="storageBucket.name"
        :bucket-id="storageBucket.id"
        :allow-download="selectedDataObjectPreviewEnabled"
        @close="closeModals"
      />
    </div>

    <!-- MODAL: Storage Object Rename -->
    <div v-if="selectedDataObject && storageBucket && storageBucket.id">
      <DataObjectRename
        :bucket-id="storageBucket.id"
        :data-object="selectedDataObject"
        :show="showModalDataObjectRename"
        @close="closeModals"
        @close-and-refresh="onDataObjectRenameSuccess"
      />
    </div>

    <!-- MODAL: Storage Object Delete -->
    <div v-if="selectedDataObject && storageBucket && storageBucket.id">
      <DataObjectDelete
        :bucket-id="storageBucket.id"
        :data-object="selectedDataObject"
        :show="showModalDataObjectDelete"
        @close="closeModals"
        @close-and-refresh="onDataObjectDeleteSuccess"
      />
    </div>

    <!-- MODAL: New Sample -->
    <div
      v-if="
        newSampleEnabled &&
        storageBucket &&
        storageBucket.id &&
        showModalSampleCreate
      "
    >
      <SampleForm
        :bucket-id="storageBucket.id"
        :sample-id="null"
        :sample-name="newSampleName"
        :sample-description="newSampleDescription"
        :sample-file-r1="newSampleFileR1"
        :sample-file-r2="newSampleFileR2"
        :kit-code="newSampleKitCode"
        :warning="newSampleWarning"
        :error="newSampleError"
        :show="showModalSampleCreate"
        @close="closeModals"
        @close-and-refresh="onSampleFormSuccess"
      />
    </div>

    <!-- MODAL: New BCL to FASTQ conversion -->
    <div
      v-if="
        bclToFastqEnabled &&
        storageBucket &&
        storageBucket.id &&
        storageBucket.name &&
        conversionFileBcl &&
        showModalBclToFastq
      "
    >
      <BclToFastqNew
        :bucket-id="storageBucket.id"
        :bucket-name="storageBucket.name"
        :file-bcl="conversionFileBcl"
        :file-csv="conversionFileCsv"
        :default-name="conversionDefaultName"
        :show="showModalBclToFastq"
        @close="closeModals"
        @close-and-refresh="onBclToFastqSuccess"
      />
    </div>

    <!-- MODAL: Bucket Expiration -->
    <div v-if="storageBucket && storageBucket.id && showBucketExpirationModal">
      <b-modal v-model="showBucketExpirationModal">
        <!-- Modal title -->
        <template #title>
          <font-awesome-icon icon="hourglass-end" class="me-2" />
          Data Expiration
        </template>
        <!-- Modal content-->
        <div>
          <div v-if="storageBucket.expiresAt">
            <p>
              This data is set to expire on
              <strong>{{ formatDate(storageBucket.expiresAt) }}</strong
              >.
            </p>
            <p>
              When data expires, all files contained within it will be
              permanently deleted and cannot be recovered.
            </p>
            <p>
              To extend the expiration date, please contact your account manager
              or our technical support team.
            </p>
          </div>
          <p v-else>This data is not set to expire.</p>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<style scoped>
/* Scoped Styles */
div.file-toolbar {
  background: #eee;
  border: solid 1px #ddd;
  border-radius: 5px !important;
  margin-bottom: 1rem;
}
div.breadcrumbs {
  font-size: 0.8rem;
}
svg.file-browser {
  color: #666;
}
.browser-table {
  font-size: 0.8rem;
}
:deep(.min-width) {
  width: 1px !important;
  white-space: nowrap;
}
</style>
